import type { AnswerSlot } from '../../types';

interface YesNoStatementsProps {
  slots: AnswerSlot[];
  selections: Record<number, string>;
  onSelect: (slotId: number, value: string) => void;
  revealed: boolean;
  hintRevealed?: boolean;
}

function buttonClass(
  choice: string,
  selected: string,
  correctAnswer: string,
  revealed: boolean,
  hintRevealed: boolean,
): string {
  const isSelected = selected === choice;
  const isCorrectChoice = correctAnswer === choice;

  if (revealed) {
    if (isCorrectChoice) {
      return 'border-green-500 bg-green-50 text-green-800 dark:border-green-500 dark:bg-green-900/20 dark:text-green-300';
    }
    if (isSelected && !isCorrectChoice) {
      return 'border-red-500 bg-red-50 text-red-800 dark:border-red-500 dark:bg-red-900/20 dark:text-red-300';
    }
    return 'border-slate-200 bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-600';
  }

  if (hintRevealed && isCorrectChoice) {
    return 'border-green-300 bg-green-50/60 dark:border-green-600 dark:bg-green-900/10 text-green-700 dark:text-green-300';
  }

  if (isSelected) {
    return 'border-primary bg-primary/10 ring-2 ring-primary/30 text-primary dark:border-primary dark:bg-primary/20';
  }

  return 'border-slate-200 bg-white hover:border-primary hover:bg-primary/5 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-primary';
}

export default function YesNoStatements({
  slots,
  selections,
  onSelect,
  revealed,
  hintRevealed = false,
}: YesNoStatementsProps) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">
        Select Yes or No for each statement:
      </p>

      {slots.map((slot) => {
        const selected = selections[slot.slot_id] ?? '';

        return (
          <div
            key={slot.slot_id}
            className="flex flex-col gap-2 sm:flex-row sm:items-center"
          >
            {/* Statement text */}
            <p className="flex-1 text-sm leading-relaxed text-slate-800 dark:text-slate-200 sm:mr-3">
              {slot.slot_text}
            </p>

            {/* Yes / No buttons */}
            <div className="flex gap-2 shrink-0">
              {slot.choices.map((choice) => (
                <button
                  key={choice}
                  type="button"
                  disabled={revealed}
                  onClick={() => onSelect(slot.slot_id, choice)}
                  aria-pressed={selected === choice}
                  className={`min-w-[4rem] rounded-xl border-2 px-4 py-2.5 text-sm font-semibold text-center transition-all
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40
                    ${buttonClass(choice, selected, slot.correct_answer, revealed, hintRevealed)}`}
                >
                  {choice}
                  {revealed && choice === slot.correct_answer && ' \u2713'}
                  {revealed && selected === choice && choice !== slot.correct_answer && ' \u2717'}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
